import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { Shell } from "@/components/Shell";
import { StatusBadge } from "@/components/StatusBadge";
import { ArrowLeft, Zap, Mail, Phone, MapPin, Route, Send, CheckCircle2, AlertTriangle, Trash2, Star, FileText, Download } from "lucide-react";
import { useNavigate } from "react-router-dom";

const SITUATION_LABELS = {
  "J'ai de l'expérience mais pas de date": "Besoin d'une date",
  "J'ai échoué et je veux passer en campagne": "Passer en campagne",
  "Je veux un permis accéléré": "Permis accéléré",
};

export default function AdminLeadDetail() {
  const { leadId } = useParams();
  const navigate = useNavigate();
  const [lead, setLead] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [note, setNote] = useState("");
  const [reminder, setReminder] = useState({ text: "", due_date: "" });
  const [sortMode, setSortMode] = useState("pertinence"); // pertinence | commission

  const load = useCallback(async () => {
    try {
      const [d, sg] = await Promise.all([
        api.get(`/admin/leads/${leadId}`),
        api.get(`/admin/leads/${leadId}/suggestions`),
      ]);
      setLead(d.data);
      setSuggestions(sg.data);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || e.message);
    }
  }, [leadId]);

  useEffect(() => {
    load();
  }, [load]);

  const run = async (fn, msg) => {
    try {
      await fn();
      toast.success(msg);
      load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || e.message);
    }
  };

  if (!lead)
    return (
      <Shell role="admin">
        <p className="p-10 text-neutral-500">Chargement…</p>
      </Shell>
    );

  return (
    <Shell role="admin">
      <div className="px-5 py-8 sm:px-8 max-w-5xl">
        <Link
          to="/admin"
          data-testid="back-to-leads-link"
          className="inline-flex items-center gap-2 text-sm font-semibold text-neutral-500 hover:text-neutral-900 transition-colors"
        >
          <ArrowLeft className="h-4 w-4" /> Tous les leads
        </Link>

        <div className="mt-5 flex flex-wrap items-center gap-3">
          <h1 className="text-3xl font-extrabold tracking-tighter" data-testid="lead-detail-name">
            {lead.first_name} {lead.last_name}
          </h1>
          <StatusBadge status={lead.status} />
          {lead.situation && (
            <span
              data-testid="lead-detail-situation"
              className="inline-flex items-center rounded-full border border-brand/30 bg-brand/10 px-2.5 py-0.5 text-xs font-bold text-brand"
            >
              {SITUATION_LABELS[lead.situation] || lead.situation}
            </span>
          )}
          <button
            data-testid="delete-lead-btn"
            onClick={async () => {
              if (!window.confirm(`Supprimer définitivement ${lead.first_name} ${lead.last_name} ?`)) return;
              try {
                await api.delete(`/admin/leads/${leadId}`);
                toast.success("Lead supprimé");
                navigate("/admin");
              } catch (e) {
                toast.error(apiError(e.response?.data?.detail) || e.message);
              }
            }}
            className="ml-auto inline-flex items-center gap-2 rounded-md border border-neutral-300 px-3 py-1.5 text-xs font-bold text-neutral-600 transition-colors hover:border-red-400 hover:text-red-600"
          >
            <Trash2 className="h-3.5 w-3.5" /> Supprimer ce lead
          </button>
          {lead.urgent && (
            <span
              data-testid="lead-detail-urgent"
              className="inline-flex items-center gap-1.5 rounded-full bg-orange-100 px-2.5 py-0.5 text-xs font-bold text-orange-700"
            >
              <Zap className="h-3 w-3" /> Urgent
            </span>
          )}
        </div>

        <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="border border-neutral-200 rounded-lg bg-white p-5">
            <p className="text-xs uppercase tracking-[0.14em] text-neutral-400">Coordonnées & demande</p>
            <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
              <p className="flex items-center gap-2">
                <Mail className="h-4 w-4 text-brand" /> {lead.email}
              </p>
              <p className="flex items-center gap-2 whitespace-nowrap">
                <Phone className="h-4 w-4 shrink-0 text-brand" />{" "}
                <a href={`tel:${lead.phone}`} className="hover:text-brand hover:underline" data-testid="lead-detail-phone">
                  {lead.phone}
                </a>
              </p>
              <p className="flex items-center gap-2">
                <MapPin className="h-4 w-4 text-brand" /> {lead.city} ({lead.postal_code})
              </p>
              <p className="flex items-center gap-2">
                <Route className="h-4 w-4 text-brand" /> {lead.area_preference || "—"}
              </p>
            </div>
            <dl className="mt-5 space-y-2 text-sm">
              {lead.situation && (
                <div data-testid="lead-detail-situation-row">
                  <dt className="text-neutral-500">Situation</dt>
                  <dd className="font-semibold text-brand">{SITUATION_LABELS[lead.situation] || lead.situation}</dd>
                </div>
              )}
              <div>
                <dt className="text-neutral-500">Permis</dt>
                <dd className="font-semibold">{lead.license_type}</dd>
              </div>
              <div>
                <dt className="text-neutral-500">Besoin / Délai</dt>
                <dd className="font-semibold">
                  {lead.need_type || "—"} · {lead.urgency || "—"}
                </dd>
              </div>
              {lead.previous_school && (
                <div>
                  <dt className="text-neutral-500">Ancienne auto-école</dt>
                  <dd className="font-semibold" data-testid="lead-previous-school">{lead.previous_school}</dd>
                </div>
              )}
              {lead.previous_school_rating > 0 && (
                <div>
                  <dt className="text-neutral-500">Avis élève (ancienne auto-école)</dt>
                  <dd className="flex items-center gap-1" data-testid="lead-prev-rating">
                    {[1, 2, 3, 4, 5].map((n) => (
                      <Star
                        key={n}
                        className={`h-4 w-4 ${
                          n <= lead.previous_school_rating ? "fill-amber-400 text-amber-400" : "text-neutral-300"
                        }`}
                      />
                    ))}
                  </dd>
                </div>
              )}
              {lead.previous_school_review && (
                <div>
                  <dt className="text-neutral-500">Commentaire élève</dt>
                  <dd className="font-semibold italic" data-testid="lead-prev-review">« {lead.previous_school_review} »</dd>
                </div>
              )}
              <div>
                <dt className="text-neutral-500">Zones souhaitées</dt>
                <dd className="font-semibold">{(lead.target_zones || []).join(", ") || "—"}</dd>
              </div>
              <div>
                <dt className="text-neutral-500">Auto-école attribuée</dt>
                <dd className="font-semibold">{lead.assigned_school_name || "Non attribuée"}</dd>
              </div>
              {(lead.school_status || lead.student_status) && (
                <div>
                  <dt className="text-neutral-500">Réponses</dt>
                  <dd className="font-semibold">
                    École : {lead.school_status || "—"} · Élève : {lead.student_status || "—"}
                  </dd>
                </div>
              )}
              {lead.message && (
                <div>
                  <dt className="text-neutral-500">Message de l'élève</dt>
                  <dd>{lead.message}</dd>
                </div>
              )}
            </dl>
          </div>

          {(lead.documents || []).length > 0 && (
            <div className="border border-neutral-200 rounded-lg bg-white p-5" data-testid="lead-documents">
              <p className="text-xs uppercase tracking-[0.14em] text-neutral-400 flex items-center gap-2"><FileText className="h-4 w-4 text-brand" /> Documents de l'élève ({lead.documents.length})</p>
              <ul className="mt-3 space-y-2">
                {lead.documents.map((d) => (
                  <li key={d.id} className="flex items-center justify-between gap-2 rounded-lg bg-neutral-50 px-3 py-2 text-sm">
                    <span className="min-w-0 truncate text-neutral-700">{d.filename}</span>
                    <a
                      href={`${process.env.REACT_APP_BACKEND_URL}/api/admin/leads/${leadId}/documents/${d.id}`}
                      target="_blank" rel="noreferrer"
                      data-testid={`lead-doc-${d.id}`}
                      className="inline-flex shrink-0 items-center gap-1 rounded-lg bg-brand/10 px-3 py-1.5 text-xs font-bold text-brand hover:bg-brand/20"
                    >
                      <Download className="h-3.5 w-3.5" /> Ouvrir
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="border border-neutral-200 rounded-lg bg-white p-5">
            <p className="text-xs uppercase tracking-[0.14em] text-neutral-400">Notes privées</p>
            <div className="mt-3 flex gap-2">
              <input
                data-testid="note-input"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Ex : appelé le 12/06, veut le samedi…"
                className="flex-1 rounded-lg border border-neutral-300 px-3 py-2 text-sm outline-none focus:border-brand transition-colors"
              />
              <button
                data-testid="add-note-btn"
                onClick={() =>
                  note.trim() &&
                  run(async () => {
                    await api.post(`/admin/leads/${leadId}/notes`, { text: note.trim() });
                    setNote("");
                  }, "Note ajoutée")
                }
                className="rounded-md bg-neutral-900 px-4 text-sm font-bold text-white transition-colors hover:bg-brand"
              >
                Ajouter
              </button>
            </div>
            <ul className="mt-4 space-y-2" data-testid="notes-list">
              {(lead.notes || []).length === 0 && <li className="text-sm text-neutral-500">Aucune note.</li>}
              {(lead.notes || []).map((n) => (
                <li key={n.id} className="flex items-start justify-between gap-3 rounded-md bg-neutral-50 px-3 py-2 text-sm">
                  <span>
                    {n.text}
                    <span className="block text-xs text-neutral-400">
                      {new Date(n.created_at).toLocaleString("fr-FR")} · {n.author}
                    </span>
                  </span>
                  <button
                    data-testid={`delete-note-${n.id}`}
                    onClick={() => run(() => api.delete(`/admin/leads/${leadId}/notes/${n.id}`), "Note supprimée")}
                    className="text-neutral-400 hover:text-red-600 transition-colors"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </li>
              ))}
            </ul>

            <p className="mt-7 text-xs uppercase tracking-[0.14em] text-neutral-400">Rappels à faire</p>
            <div className="mt-3 flex flex-col sm:flex-row gap-2">
              <input
                data-testid="reminder-text-input"
                value={reminder.text}
                onChange={(e) => setReminder({ ...reminder, text: e.target.value })}
                placeholder="Ex : relancer l'élève"
                className="flex-1 rounded-lg border border-neutral-300 px-3 py-2 text-sm outline-none focus:border-brand transition-colors"
              />
              <input
                data-testid="reminder-date-input"
                type="date"
                value={reminder.due_date}
                onChange={(e) => setReminder({ ...reminder, due_date: e.target.value })}
                className="rounded-lg border border-neutral-300 px-3 py-2 text-sm outline-none focus:border-brand transition-colors"
              />
              <button
                data-testid="add-reminder-btn"
                onClick={() =>
                  reminder.text.trim() &&
                  run(async () => {
                    await api.post(`/admin/leads/${leadId}/reminders`, reminder);
                    setReminder({ text: "", due_date: "" });
                  }, "Rappel ajouté")
                }
                className="rounded-md bg-neutral-900 px-4 py-2 text-sm font-bold text-white transition-colors hover:bg-brand"
              >
                Ajouter
              </button>
            </div>
            <ul className="mt-4 space-y-2" data-testid="reminders-list">
              {(lead.reminders || []).length === 0 && <li className="text-sm text-neutral-500">Aucun rappel.</li>}
              {(lead.reminders || []).map((r) => (
                <li key={r.id} className="flex items-center gap-3 rounded-md bg-neutral-50 px-3 py-2 text-sm">
                  <input
                    data-testid={`toggle-reminder-${r.id}`}
                    type="checkbox"
                    checked={!!r.done}
                    onChange={() =>
                      run(() => api.patch(`/admin/leads/${leadId}/reminders/${r.id}`), "Rappel mis à jour")
                    }
                    className="h-4 w-4 accent-brand"
                  />
                  <span className={r.done ? "line-through text-neutral-400" : ""}>
                    {r.text}
                    {r.due_date && <span className="ml-2 text-xs text-neutral-500">échéance {r.due_date}</span>}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="mt-6 border border-neutral-200 rounded-lg bg-white p-5" data-testid="suggestions-panel">
          <p className="text-xs uppercase tracking-[0.14em] text-neutral-400">
            Auto-écoles suggérées pour ses zones
          </p>
          <p className="mt-1 text-sm text-neutral-500">
            Classement indicatif selon les zones demandées, la ville et le code postal. Le choix final reste le tien.
          </p>
          <div className="mt-3 inline-flex rounded-lg border border-neutral-200 p-0.5 text-xs font-semibold" data-testid="suggestions-sort">
            <button
              data-testid="sort-pertinence-btn"
              onClick={() => setSortMode("pertinence")}
              className={`rounded-md px-3 py-1.5 transition-colors ${
                sortMode === "pertinence" ? "bg-neutral-900 text-white" : "text-neutral-500 hover:text-neutral-900"
              }`}
            >
              Pertinence
            </button>
            <button
              data-testid="sort-commission-btn"
              onClick={() => setSortMode("commission")}
              className={`rounded-md px-3 py-1.5 transition-colors ${
                sortMode === "commission" ? "bg-brand text-white" : "text-neutral-500 hover:text-neutral-900"
              }`}
            >
              Gain le plus élevé
            </button>
          </div>
          <ul className="mt-4 space-y-2">
            {suggestions.length === 0 && <li className="text-sm text-neutral-500">Aucune auto-école partenaire.</li>}
            {[...suggestions]
              .sort((a, b) =>
                sortMode === "commission"
                  ? (b.commission_amount || 0) - (a.commission_amount || 0) || b.score - a.score
                  : b.score - a.score
              )
              .map((s) => (
              <li
                key={s.id}
                data-testid={`suggestion-${s.id}`}
                className="flex flex-wrap items-center gap-3 rounded-md border border-neutral-200 px-4 py-3"
              >
                <span className="font-semibold">
                  {s.name}
                  <span className="block text-xs font-normal text-neutral-500">
                    {s.city} {s.postal_code} · {s.coverage || "zone non renseignée"}
                  </span>
                </span>
                <span className="flex flex-wrap gap-1.5">
                  {s.reasons.map((r) => (
                    <span key={r} className="rounded-full bg-neutral-100 px-2 py-0.5 text-[11px] font-semibold text-neutral-600">
                      {r}
                    </span>
                  ))}
                </span>
                <span
                  className="ml-auto rounded-md bg-emerald-50 px-2.5 py-1 text-xs font-bold text-emerald-700"
                  data-testid={`suggestion-gain-${s.id}`}
                  title="Ce que tu gagnes si l'élève s'inscrit"
                >
                  {s.commission_type === "pourcentage" ? `${s.commission_amount} %` : `+${s.commission_amount} €`}
                </span>
                <button
                  data-testid={`assign-suggestion-${s.id}`}
                  onClick={() => run(() => api.post(`/admin/leads/${leadId}/assign/${s.id}`), `Lead attribué à ${s.name}`)}
                  className={`rounded-md px-4 py-2 text-xs font-bold transition-colors ${
                    lead.assigned_school_id === s.id
                      ? "bg-emerald-600 text-white"
                      : "bg-neutral-900 text-white hover:bg-brand"
                  }`}
                >
                  {lead.assigned_school_id === s.id ? "Attribuée" : "Attribuer"}
                </button>
              </li>
            ))}
          </ul>
        </div>

        <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="border border-neutral-200 rounded-lg bg-white p-5">
            <p className="text-xs uppercase tracking-[0.14em] text-neutral-400">Historique</p>
            <ul className="mt-4 space-y-2 text-sm text-neutral-600" data-testid="lead-timeline">
              {(lead.timeline || []).map((t, i) => (
                <li key={i}>
                  <span className="text-neutral-400">{new Date(t.at).toLocaleString("fr-FR")}</span> — {t.event}
                </li>
              ))}
            </ul>
          </div>

          <div className="border border-neutral-200 rounded-lg bg-white p-5">
            <div className="flex items-center justify-between gap-3">
              <p className="text-xs uppercase tracking-[0.14em] text-neutral-400">Emails de ce lead</p>
              <button
                data-testid="lead-relance-btn"
                onClick={() => run(() => api.post(`/admin/leads/${leadId}/relance`), "Relance déclenchée")}
                className="inline-flex items-center gap-1.5 rounded-md border border-neutral-300 px-3 py-1.5 text-xs font-bold transition-colors hover:border-brand hover:text-brand"
              >
                <Send className="h-3.5 w-3.5" /> Relancer l'élève
              </button>
            </div>
            <ul className="mt-4 space-y-2 text-sm" data-testid="lead-emails">
              {(lead.emails || []).length === 0 && <li className="text-neutral-500">Aucun email envoyé.</li>}
              {(lead.emails || []).map((e) => (
                <li key={e.id} className="flex items-start gap-2">
                  {e.status === "sent" ? (
                    <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-500" />
                  ) : (
                    <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-red-500" />
                  )}
                  <span>
                    {e.subject}
                    <span className="block text-xs text-neutral-400">
                      {new Date(e.created_at).toLocaleString("fr-FR")} · {e.to}
                    </span>
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </Shell>
  );
}
